import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { Grid } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import EditorJS from '@editorjs/editorjs';
import { useFormContext } from 'react-hook-form';
import uuid from 'uuid';

import { NameScriptInstruction, InstructionTypes } from '../../../../types/instructions';
import { selectNameScriptInstruction } from '../../../../store/modules/script-editor/selectors';
import { setOpenNameScriptInstructionModal } from '../../../../store/modules/script-editor/actions';
import { parseJsonToEditorStateWithInstruction } from '../../../../services/editorJS/utils';
import {
  emitInstructionEventByEditor,
  removeInstructionElement,
} from '../utils';
import { InstructionEvent } from '../types';
import { NameScript } from '../../../../configs/instructionConfig';
import {
  ModalComponent,
  InputComponent,
  SelectComponent,
} from '../components';
import DuplicateBlock from '../../../../services/editorJS/DuplicateBlock';
import { notify } from '../../../../store/modules/notifications/actions';

type Props = {
  instanceEditor: EditorJS | undefined;
};

const scriptFieldName = 'script';
const defaultState = {
  comment: '',
  name: 0,
};

const NameScriptInstructionModal = ({ instanceEditor }: Props) => {
  const dispatch = useDispatch();
  const { getValues, setValue } = useFormContext();
  const { isOpenModal, instructionId, isInline } = useSelector(selectNameScriptInstruction);
  const [comment, setComment] = useState(defaultState.comment);
  const [name, setName] = useState(defaultState.name);
  const [initialState, setInitialState] = useState(defaultState);

  useEffect(() => {
    (async () => {
      if (instructionId) {
        const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
        const instruction = scriptData.instructions.find(
          (item) => item.id === instructionId && item.type === 'nameScript',
        );
        if (instruction) {
          const { data } = instruction as NameScriptInstruction;
          setComment(instruction.comment);
          setName(data.name);
          setInitialState({
            comment: instruction.comment,
            name: data.name,
          });
        } else {
          setComment(defaultState.comment);
          setName(defaultState.name);
          setInitialState(defaultState);
        }
      } else {
        setComment(defaultState.comment);
        setName(defaultState.name);
        setInitialState(defaultState);
      }
    })();
  }, [
    setComment,
    setName,
    instructionId,
    getValues,
    setInitialState,
  ]);

  const closeHandler = useCallback(() => {
    if (instanceEditor) {
      emitInstructionEventByEditor(
        instanceEditor,
        InstructionEvent.SET_NAME_SCRIPT_INSTRUCTION_SELECTION,
        {},
      );
    }
    dispatch(setOpenNameScriptInstructionModal(false));
  }, [dispatch, instanceEditor]);

  const getSummary = useCallback(() => {
    const nameScript = NameScript.find((item) => item.value === name);
    if (nameScript) {
      return nameScript.title;
    }
    if (comment.trim()) {
      return comment;
    }
    return 'Name in script';
  }, [comment, name]);

  const saveHandler = useCallback(() => {
    (async () => {
      if (instanceEditor) {
        const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
        const instructionIndex = scriptData.instructions.findIndex(
          (item) => item.id === instructionId && item.type === InstructionTypes.nameScriptInstruction,
        );
        const nameInstruction: NameScriptInstruction = {
          id: instructionId,
          type: InstructionTypes.nameScriptInstruction,
          comment,
          needsInfo: false,
          data: {
            name,
          },
        };
        const summary = getSummary();
        if (instructionIndex !== -1) {
          scriptData.instructions[instructionIndex] = nameInstruction;
          instanceEditor.events.emit(InstructionEvent.SUBMIT_DATA, {});
        } else {
          const id = (instructionId.trim()) ? instructionId : uuid();
          nameInstruction.id = id;
          scriptData.instructions.push(nameInstruction);
          if (isInline) {
            emitInstructionEventByEditor(
              instanceEditor,
              InstructionEvent.SAVE_NAME_SCRIPT_INSTRUCTION,
              { instructionId: id, data: name.toString() },
            );
          }
        }
        if (!isInline) {
          emitInstructionEventByEditor(
            instanceEditor,
            InstructionEvent.SAVE_NAME_SCRIPT_INSTRUCTION_BLOCK,
            { instructionId: nameInstruction.id, text: summary, data: name.toString() },
          );
        }
        setValue(scriptFieldName, JSON.stringify(scriptData));
        closeHandler();
      }
    })();
  }, [
    instanceEditor,
    getValues,
    setValue,
    instructionId,
    closeHandler,
    comment,
    name,
    isInline,
    getSummary,
  ]);

  const cancelHandler = useCallback(() => {
    (async () => {
      setComment(initialState.comment);
      setName(initialState.name);
      const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
      const isInstructionCreated = scriptData.instructions.some(
        (item) => item.id === instructionId && item.type === InstructionTypes.documentInstruction,
      ) || scriptData.blocks.some((item) => item.data.instructionId === instructionId);
      if (instanceEditor) {
        if (!isInstructionCreated) {
          emitInstructionEventByEditor(
            instanceEditor,
            InstructionEvent.REMOVE_INSTRUCTION_BLOCK_WITHOUT_SAVING,
            {},
          );
        }
        emitInstructionEventByEditor(
          instanceEditor,
          InstructionEvent.CANCEL_INSTRUCTION,
          {},
        );
      }
      closeHandler();
    })();
  }, [
    closeHandler,
    setComment,
    setName,
    initialState,
    instanceEditor,
    instructionId,
    getValues,
  ]);

  const removeHandler = useCallback(() => {
    if (!isInline && instanceEditor) {
      emitInstructionEventByEditor(
        instanceEditor,
        InstructionEvent.REMOVE_INSTRUCTION_BLOCK,
        {},
      );
    } else {
      removeInstructionElement({ instructionId, isInline })
        .then(() => {
          if (instanceEditor) {
            instanceEditor.events.emit(InstructionEvent.SUBMIT_DATA, {});
          }
        });
    }
    closeHandler();
  }, [closeHandler, instructionId, isInline, instanceEditor]);

  const duplicate = useCallback(() => {
    const scriptData = parseJsonToEditorStateWithInstruction(getValues().script);
    const notifHandler = () => dispatch(
      notify({
        type: 'success',
        icon: 'copy',
        message: 'Instruction duplicated.',
      }),
    );
    if (instanceEditor) {
      const duplicateBlockService = new DuplicateBlock(instanceEditor, notifHandler, setValue, instructionId, scriptData);
      if (!isInline) {
        duplicateBlockService.duplicateBlock();
      } else {
        duplicateBlockService.duplicateInline();
      }
    }
  }, [instanceEditor, getValues, instructionId, isInline, setValue, dispatch]);

  const showKebabMenu = useMemo(() => {
    const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
    return scriptData.instructions.some(
      (item) => item.id === instructionId,
    );
  }, [getValues, instructionId]);

  return (
    <ModalComponent
      open={isOpenModal}
      title={getSummary()}
      saveHandler={saveHandler}
      cancelHandler={cancelHandler}
      deleteHandler={removeHandler}
      duplicate={duplicate}
      showKebabMenu={showKebabMenu}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <InputComponent
            type="textarea"
            label="Comment"
            value={comment}
            onChange={setComment}
            autoFocus
          />
        </Grid>
        <Grid item xs={6}>
          <SelectComponent
            label="Name"
            value={name}
            items={NameScript}
            changeHandler={setName}
            isNone={false}
          />
        </Grid>
      </Grid>
    </ModalComponent>
  );
};

export default NameScriptInstructionModal;
